import React, { useMemo, useRef, useState } from "react";
import {
  useIonViewWillEnter,
  useIonViewWillLeave,
  IonSegment,
  IonSegmentButton,
  IonIcon,
} from "@ionic/react";
import { calendarOutline, barChartOutline } from "ionicons/icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";
import {
  TITLE_MAP,
  SEQUENCE_MAP,
  SEQUENCE_RENDER_MAP,
  ALL_ISLANDS,
} from "./IslandRegistry";
import { useParams } from "react-router";

import EvolutionChart from "./EvolutionChart";
import EvolutionCalendar from "./EvolutionCalendar";
import { useIslandVisibility } from "../../components/organisms/ResonanceContext/IslandVisibilityContext";

type SegVal = "graph" | "calendar";

const DisplayEvolution: React.FC = () => {
  const { scores } = useResonanceData();
  const { setResonanceLayout } = useResonanceLayout();
  const { getTitle } = useIslandVisibility();

  // islandKey is present when coming from /island/:title/evolution,
  // absent for the global evolution view
  const { title: islandKey } = useParams<{ title?: string }>();

  const focusedIsland = islandKey ? ALL_ISLANDS[islandKey] : undefined;
  const isFocused = !!focusedIsland;

  const [activeIndex, setActiveIndex] = useState(0);
  const [animateTick, setAnimateTick] = useState(0);
  const [viewActive, setViewActive] = useState(false);
  const [segmentBySeq, setSegmentBySeq] = useState<Record<number, SegVal>>({});

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      welcomePage: false,
      title: isFocused && islandKey ? getTitle(islandKey) : "Mon Evolution",
    });
    setViewActive(true);
    setAnimateTick((t) => t + 1);
  });

  useIonViewWillLeave(() => {
    setViewActive(false);
  });

  // Group scores by sequenceId
  const groups = useMemo(() => {
    const map = new Map<number, { date: Date; score: number }[]>();

    for (const s of scores) {
      const arr = map.get(s.sequenceId) ?? [];
      arr.push({ date: new Date(s.doneAt), score: s.score });
      map.set(s.sequenceId, arr);
    }

    const all = Array.from(map.entries())
      .map(([id, data]) => ({
        id,
        data: data.sort((a, b) => a.date.getTime() - b.date.getTime()),
      }))
      .sort((a, b) => a.id - b.id);

    // If we’re not focused on a specific island, show all sequences (global view)
    if (!islandKey || !focusedIsland) {
      return all;
    }

    // When focused on an island, keep ONLY this island’s main + inline sequences
    const wantedIds: number[] = [];
    if (focusedIsland.main.sequenceId != null) {
      wantedIds.push(focusedIsland.main.sequenceId);
    }
    if (focusedIsland.inline?.sequenceId != null) {
      wantedIds.push(focusedIsland.inline.sequenceId);
    }

    if (!wantedIds.length) {
      return all;
    }

    return all.filter((g) => wantedIds.includes(g.id));
  }, [scores, islandKey, focusedIsland]);

  const swiperRef = useRef<import("swiper").Swiper | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);

  const handlePrev = () => swiperRef.current?.slidePrev();
  const handleNext = () => swiperRef.current?.slideNext();

  const activeSequenceId = groups[activeIndex]?.id;

  const seqToIslandKey = useMemo(() => {
    const m = new Map<number, string>();

    Object.entries(ALL_ISLANDS).forEach(([islandKey, def]) => {
      if (def.main?.sequenceId != null) m.set(def.main.sequenceId, islandKey);
      if (def.inline?.sequenceId != null)
        m.set(def.inline.sequenceId, islandKey);
    });

    return m;
  }, []);

  // Which sequenceIds are currently shown (for the focused island / global view)
  const visibleSeqIds = useMemo(
    () => new Set(groups.map((g) => g.id)),
    [groups],
  );

  const activeTitle =
    activeSequenceId != null
      ? (() => {
          const islandKeyFromSeq = seqToIslandKey.get(activeSequenceId);

          // Fallback if we cannot map it to an island
          if (!islandKeyFromSeq) return `Questionnaire ${activeSequenceId}`;

          const islandTitle = getTitle(islandKeyFromSeq);

          // If this island can show BOTH main + inline in this current view, add questionnaire label
          const def = ALL_ISLANDS[islandKeyFromSeq];
          const mainId = def?.main?.sequenceId ?? null;
          const inlineId = def?.inline?.sequenceId ?? null;

          const hasBothInView =
            mainId != null &&
            inlineId != null &&
            visibleSeqIds.has(mainId) &&
            visibleSeqIds.has(inlineId);

          if (!hasBothInView) return islandTitle;

          // Questionnaire title: use your existing render map if it has a label,
          // otherwise fall back to a generic "Questionnaire X"
          const qTitle =
            // @ts-ignore (in case your cfg doesn't type "title")
            (SEQUENCE_RENDER_MAP[activeSequenceId] as any)?.title ??
            `Questionnaire ${activeSequenceId}`;

          return `${islandTitle} — ${qTitle}`;
        })()
      : "Aucun questionnaire";

  // ---- Island icon strip under charts ----
  const ICON_SIZE = 56;

  const islandKeyByGroupIndex = useMemo(() => {
    return groups.map((g) => seqToIslandKey.get(g.id) ?? null);
  }, [groups, seqToIslandKey]);

  const islandIcons = useMemo(() => {
    const seen = new Set<string>();
    const out: { islandKey: string; firstIndex: number }[] = [];

    islandKeyByGroupIndex.forEach((k, idx) => {
      if (!k) return;
      if (seen.has(k)) return;
      seen.add(k);
      out.push({ islandKey: k, firstIndex: idx });
    });

    return out;
  }, [islandKeyByGroupIndex]);

  const activeIslandKey = islandKeyByGroupIndex[activeIndex] ?? null;

  const handleIslandIconClick = (firstIndex: number) => {
    swiperRef.current?.slideTo(firstIndex);
  };

  return (
    <ResonanceLayout isConnected>
      <>
        <style>{`
          .evo-wrapper { width:100%; max-width:640px; margin:0 auto; padding:1rem 0 0.5rem; text-align:center; }
          .evo-header { display:grid; grid-template-columns:2.5rem 1fr 2.5rem; align-items:center; gap:0.5rem; margin-bottom:0.25rem; }
          .evo-title { margin:0; font-size:1.2rem; line-height:1; }
          .evo-arrow { border:none; background:transparent; font-size:1.5rem; cursor:pointer; padding:0.25rem; line-height:1; }
          .evo-swiper { width:100%; }
          .evo-swiper .swiper-pagination { position:static; margin-top:0.25rem; }
          .evo-segment{
  margin: 0.25rem 0 0.5rem;
  display:flex;
  justify-content:center;
}


          .evo-island-grid {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            gap: 10px;
            margin-top: 12px;
            padding: 6px 8px 0;
          }

          .evo-island-btn {
            width: ${ICON_SIZE}px;
            height: ${ICON_SIZE}px;
            border-radius: 999px;
            border: 1px solid rgba(0,0,0,0.10);
            background: rgba(255,255,255,0.9);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 6px 14px rgba(0,0,0,0.12);
            cursor: pointer;
            padding: 0;
          }

          .evo-island-btn:active {
            transform: scale(0.98);
          }

          .evo-island-btn--active {
            outline: 3px solid rgba(148, 241, 239, 0.75);
            outline-offset: 2px;
          }

          .evo-island-svg {
            width: ${Math.round(ICON_SIZE * 0.78)}px;
            height: ${Math.round(ICON_SIZE * 0.78)}px;
            display: block;
          }

          /* Vincent-style pill segment */
.top-pill{
  width: 240px;
  border-radius: 999px;
  padding: 0;
  --background: transparent;
  background: #fff;
  box-shadow: 0 10px 22px rgba(0,0,0,0.14);
  overflow: hidden;
}

.top-pill-btn{
  flex: 1;
  min-height: 54px;
  --padding-start: 0;
  --padding-end: 0;
}

/* Hide Ionic segment indicator */
.top-pill::part(indicator){ display:none !important; }
.top-pill::part(indicator-background){ display:none !important; }
.top-pill{ --indicator-color: transparent !important; --indicator-height: 0px !important; }

.top-pill-btn::part(native){
  border-radius: 0;
  background: transparent;
  color: #000;
  transition: background 200ms ease, color 200ms ease;
}

.top-pill ion-icon{
  font-size: 26px;
}

.top-pill-btn.segment-button-checked::part(native){
  background: #5b7cff;
  color: #fff;
}

.top-pill-btn:first-child.segment-button-checked::part(native){
  border-top-left-radius: 999px;
  border-bottom-left-radius: 999px;
}
.top-pill-btn:last-child.segment-button-checked::part(native){
  border-top-right-radius: 999px;
  border-bottom-right-radius: 999px;
}

/* Optional small-screen tuning (same as Vincent) */
@media (max-width: 375px) and (max-height: 667px){
  .top-pill{ width: 200px; }
  .top-pill-btn{ min-height: 44px; }
  .top-pill ion-icon{ font-size: 22px; }
}

        `}</style>

        <div className="evo-wrapper">
          <div className="evo-header">
            <button
              className="evo-arrow"
              aria-label="Précédent"
              onClick={handlePrev}
              style={{
                visibility:
                  groups.length === 0 || activeIndex === 0
                    ? "hidden"
                    : "visible",
              }}
            >
              ‹
            </button>

            <h2 className="evo-title">{activeTitle}</h2>

            <button
              className="evo-arrow"
              aria-label="Suivant"
              onClick={handleNext}
              style={{
                visibility:
                  groups.length === 0 || activeIndex === groups.length - 1
                    ? "hidden"
                    : "visible",
              }}
            >
              ›
            </button>
          </div>

          <Swiper
            className="evo-swiper"
            modules={[Navigation, Pagination, A11y]}
            slidesPerView={1}
            onSwiper={(sw) => (swiperRef.current = sw)}
            onSlideChange={(sw) => {
              setActiveIndex(sw.activeIndex);
            }}
            onSlideChangeTransitionEnd={() => setAnimateTick((t) => t + 1)}
            pagination={{ el: paginationRef.current, clickable: true }}
            onBeforeInit={(sw) => {
              // @ts-ignore
              sw.params.pagination = {
                ...(sw.params.pagination as any),
                el: paginationRef.current,
                clickable: true,
              };
            }}
          >
            {groups.map((g, idx) => {
              const renderCfg = SEQUENCE_RENDER_MAP[g.id] ?? {
                chartType: "circles" as const,
              };

              const isActive = idx === activeIndex;

              // calendar is available whenever there is a scale (emoji OR sprite)
              const hasCalendar = !!renderCfg.scale && !!renderCfg.scale.mode;

              const segVal: SegVal = hasCalendar
                ? (segmentBySeq[g.id] ?? "graph")
                : "graph";

              return (
                <SwiperSlide key={g.id}>
                  {/* Per-sequence segment (calendar only if scale exists) */}
                  <div className="evo-segment">
                    <IonSegment
                      value={segVal}
                      className="top-pill"
                      onIonChange={(e) => {
                        const val = (e.detail.value as SegVal) ?? "graph";
                        setSegmentBySeq((prev) => ({ ...prev, [g.id]: val }));
                        if (val === "graph" && isActive)
                          setAnimateTick((t) => t + 1);
                      }}
                    >
                      <IonSegmentButton
                        value="calendar"
                        className="top-pill-btn"
                        disabled={!hasCalendar}
                      >
                        <IonIcon icon={calendarOutline} />
                      </IonSegmentButton>

                      <IonSegmentButton value="graph" className="top-pill-btn">
                        <IonIcon icon={barChartOutline} />
                      </IonSegmentButton>
                    </IonSegment>
                  </div>

                  {/* Content */}
                  {viewActive &&
                    (segVal === "graph" ? (
                      <EvolutionChart
                        data={g.data}
                        renderCfg={renderCfg}
                        animateKey={isActive ? animateTick : 0}
                      />
                    ) : hasCalendar ? (
                      <EvolutionCalendar
                        data={g.data}
                        renderCfg={renderCfg} // now supports emoji + sprite
                      />
                    ) : null)}
                </SwiperSlide>
              );
            })}
          </Swiper>
          {groups.length > 1 && <div ref={paginationRef} />}
          {/* Island icons under the charts */}
          {islandIcons.length > 0 && (
            <div className="evo-island-grid">
              {islandIcons.map(({ islandKey, firstIndex }) => {
                const def = ALL_ISLANDS[islandKey];
                if (!def) return null;

                const Icon = def.Component;

                return (
                  <button
                    key={islandKey}
                    className={
                      "evo-island-btn" +
                      (activeIslandKey === islandKey
                        ? " evo-island-btn--active"
                        : "")
                    }
                    onClick={() => handleIslandIconClick(firstIndex)}
                    title={getTitle(islandKey)}
                    aria-label={getTitle(islandKey)}
                    type="button"
                  >
                    <Icon className="evo-island-svg" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </>
    </ResonanceLayout>
  );
};

export default DisplayEvolution;
